import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import ConfirmationModal from "../../../../components/ConfirmationModal";
import { adminFlagshipService, ProviderFlagship } from "../../../../services/adminFlagshipService";
import type { PortfolioListItem } from "../../../../types/productV3";

const secondaryButton = "rounded-lg border border-gray-200 dark:border-rp-separator bg-white dark:bg-rp-surface px-3 py-2 text-sm text-gray-700 dark:text-gray-300 transition-colors hover:border-gray-300 dark:hover:border-gray-600 disabled:opacity-50";
const primaryButton = "rounded-lg bg-rp-amber px-3 py-2 text-sm font-semibold text-white transition-colors hover:bg-yellow-600 disabled:opacity-50";
const inputClass = "w-full rounded-lg border border-gray-200 dark:border-rp-separator bg-white dark:bg-rp-surface-raised px-3 py-2 text-sm text-gray-900 dark:text-white focus:outline-none focus:border-rp-amber focus:ring-1 focus:ring-rp-amber";
const errorClass = "rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-3 text-sm text-red-700 dark:text-red-400";

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : "Unable to manage flagship portfolios. Please try again.";
}

function FlagshipPortfolioEditor({ provider, onSaved, onCancel }: {
  provider: ProviderFlagship;
  onSaved: (provider: ProviderFlagship) => void;
  onCancel: () => void;
}) {
  const [portfolios, setPortfolios] = useState<PortfolioListItem[]>([]);
  const [portfolioId, setPortfolioId] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retry, setRetry] = useState(0);
  const requestRef = useRef(0);
  const mountedRef = useRef(true);
  const savingRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    const requestId = ++requestRef.current;
    const controller = new AbortController();
    setLoading(true);
    setPortfolios([]);
    setPortfolioId("");
    setError(null);
    adminFlagshipService.portfolios(provider.providerId, controller.signal).then((choices) => {
      if (controller.signal.aborted || requestRef.current !== requestId) return;
      const eligiblePortfolios = choices.filter(choice => choice.visibility !== "PRIVATE");
      setPortfolios(eligiblePortfolios);
      setPortfolioId(eligiblePortfolios.some(choice => choice.id === provider.flagship?.portfolioId)
        ? String(provider.flagship?.portfolioId) : "");
    }).catch((failure: unknown) => {
      if (!controller.signal.aborted && requestRef.current === requestId) setError(errorMessage(failure));
    }).finally(() => {
      if (!controller.signal.aborted && requestRef.current === requestId) setLoading(false);
    });
    return () => { controller.abort(); };
  }, [provider.providerId, provider.flagship?.portfolioId, retry]);

  const save = async () => {
    if (!portfolioId || savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    setError(null);
    try {
      const saved = await adminFlagshipService.designate(provider.providerId, Number(portfolioId));
      if (mountedRef.current) onSaved(saved);
    } catch (failure) {
      if (mountedRef.current) setError(errorMessage(failure));
    } finally {
      savingRef.current = false;
      if (mountedRef.current) setSaving(false);
    }
  };

  return (
    <section aria-label={`Edit flagship for ${provider.providerName}`} className="mb-4 space-y-3 rounded-lg border border-gray-200 dark:border-rp-separator bg-white dark:bg-rp-surface p-4">
      <h2 className="text-lg font-bold text-gray-900 dark:text-white">Flagship for {provider.providerName}</h2>
      <p className="text-sm text-gray-600 dark:text-gray-300">Saving replaces this provider's current flagship. Only public or subscriber portfolios can be designated. Visibility can be changed afterward.</p>
      {error && <div role="alert" className={errorClass}>{error} <button className="underline" onClick={() => setRetry(count => count + 1)} disabled={saving}>Reload portfolios</button></div>}
      {loading ? <p role="status" className="text-sm text-gray-600 dark:text-gray-300">Loading portfolios...</p> : (
        <label className="block space-y-1 text-sm text-gray-600 dark:text-gray-300">
          <span>Portfolio</span>
          <select className={inputClass} value={portfolioId} onChange={event => setPortfolioId(event.target.value)} disabled={saving}>
            <option value="">Select a portfolio</option>
            {portfolios.map(portfolio => <option key={portfolio.id} value={portfolio.id}>{portfolio.title} (#{portfolio.id}, {portfolio.visibility})</option>)}
          </select>
        </label>
      )}
      {!loading && !error && portfolios.length === 0 && <p className="text-sm text-gray-600 dark:text-gray-300">This provider has no public or subscriber portfolios available to designate.</p>}
      <div className="flex gap-2">
        <button className={primaryButton} disabled={loading || saving || !portfolioId} onClick={save}>{saving ? "Saving..." : "Save flagship"}</button>
        <button className={secondaryButton} disabled={saving} onClick={onCancel}>Cancel</button>
      </div>
    </section>
  );
}

export default function FlagshipPortfoliosTab() {
  const [providers, setProviders] = useState<ProviderFlagship[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<ProviderFlagship | null>(null);
  const [removing, setRemoving] = useState<ProviderFlagship | null>(null);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState("");
  const requestRef = useRef(0);
  const mountedRef = useRef(true);
  const savingRef = useRef(false);

  const load = useCallback(async () => {
    const requestId = ++requestRef.current;
    setLoading(true);
    setError(null);
    try {
      const loaded = await adminFlagshipService.listAll();
      if (mountedRef.current && requestRef.current === requestId) setProviders(loaded);
    } catch (failure) {
      if (mountedRef.current && requestRef.current === requestId) setError(errorMessage(failure));
    } finally {
      if (mountedRef.current && requestRef.current === requestId) setLoading(false);
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    void load();
    return () => { mountedRef.current = false; };
  }, [load]);

  const remove = async () => {
    if (!removing || savingRef.current) return;
    const provider = removing;
    savingRef.current = true;
    setSaving(true);
    setError(null);
    try {
      await adminFlagshipService.remove(provider.providerId);
      if (!mountedRef.current) return;
      setProviders(current => current.map(entry => entry.providerId === provider.providerId ? { ...entry, flagship: null } : entry));
      setRemoving(null);
      setNotice(`Flagship removed for ${provider.providerName}.`);
    } catch (failure) {
      if (mountedRef.current) { setRemoving(null); setError(errorMessage(failure)); }
    } finally {
      savingRef.current = false;
      if (mountedRef.current) setSaving(false);
    }
  };

  return (
    <div className="p-6 md:p-8 bg-gray-50 dark:bg-black text-gray-900 dark:text-white transition-colors duration-300">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold tracking-tight">Flagship portfolios</h1>
          <p className="text-sm text-gray-600 dark:text-gray-300">One flagship portfolio per provider. Review every assignment here.</p>
        </div>
        <button className={secondaryButton} disabled={loading || editing !== null || removing !== null} onClick={load}>Refresh flagships</button>
      </div>
      {notice && <p role="status" className="mb-4 text-sm text-gray-600 dark:text-gray-300">{notice}</p>}
      {error && <div role="alert" className={`${errorClass} mb-4`}>{error} <button className="underline" disabled={editing !== null || removing !== null} onClick={load}>Retry</button></div>}
      {editing && <FlagshipPortfolioEditor key={editing.providerId} provider={editing} onCancel={() => setEditing(null)} onSaved={saved => {
        ++requestRef.current;
        setProviders(current => current.map(provider => provider.providerId === saved.providerId ? saved : provider));
        setEditing(null);
        setNotice(`Flagship saved for ${saved.providerName}.`);
      }} />}
      {loading ? <p role="status" className="py-12 text-sm text-gray-600 dark:text-gray-300">Loading flagship portfolios...</p> : !error || providers.length > 0 ? (
        <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-rp-separator bg-white dark:bg-rp-surface">
          <table className="w-full text-left text-sm">
            <caption className="sr-only">Flagship portfolio assignments for all providers</caption>
            <thead className="border-b border-gray-200 dark:border-rp-separator text-gray-500 dark:text-gray-400"><tr>
              <th scope="col" className="p-4">Provider</th><th scope="col" className="p-4">Flagship portfolio</th><th scope="col" className="p-4">Visibility</th><th scope="col" className="p-4">Actions</th>
            </tr></thead>
            <tbody>{providers.map(provider => <tr key={provider.providerId} className="border-b border-gray-200 dark:border-rp-separator hover:bg-gray-50 dark:hover:bg-rp-surface-raised">
              <th scope="row" className="p-4 font-semibold">{provider.providerName}</th>
              <td className="p-4">{provider.flagship ? <>
                {provider.flagship.deletedAtUtc ? <span>{provider.flagship.title} (deleted)</span> : <Link className="text-rp-amber underline" to={`/admin/products/portfolios/${provider.providerId}/${provider.flagship.portfolioId}`}>{provider.flagship.title}</Link>}
                <span className="ml-2 font-mono text-xs text-gray-500 dark:text-gray-400">#{provider.flagship.portfolioId}</span>
              </> : <span className="text-gray-500 dark:text-gray-400">Not designated</span>}</td>
              <td className="p-4 text-xs text-gray-500 dark:text-gray-400">{provider.flagship?.visibility ?? "-"}</td>
              <td className="p-4"><div className="flex gap-2">
                <button className={secondaryButton} disabled={removing !== null || editing !== null} aria-label={`${provider.flagship ? "Change" : "Designate"} flagship for ${provider.providerName}`} onClick={() => { setNotice(""); setEditing(provider); }}>{provider.flagship ? "Change" : "Designate"}</button>
                {provider.flagship && <button className={secondaryButton} disabled={removing !== null || editing !== null} aria-label={`Remove flagship for ${provider.providerName}`} onClick={() => setRemoving(provider)}>Remove</button>}
              </div></td>
            </tr>)}</tbody>
          </table>
          {providers.length === 0 && <p className="py-12 text-center text-sm text-gray-500 dark:text-gray-400">No providers found.</p>}
        </div>
      ) : null}
      <ConfirmationModal isOpen={removing !== null} onClose={() => setRemoving(null)} onConfirm={remove} title="Remove flagship portfolio?" message={`Remove the flagship designation for ${removing?.providerName ?? "this provider"}? The portfolio will remain available.`} confirmText="Remove flagship" isDestructive loading={saving} />
    </div>
  );
}
